import { useContext } from 'react'

import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

/**
 * True when the current user cannot own or persist the opened model - i.e. a
 * logged-out visitor, or a logged-in user who opened someone else's model via a
 * shared /ai/3d-models/<slug> link. Used to suppress owner-only actions and
 * settings persistence so these users do not trigger failing owner-scoped,
 * auth-only PATCHes (the "Failed to save viewer settings." toast on rotate/zoom
 * being the reported symptom).
 *
 * Reads pageConfig with the default options so the already-loaded
 * window.pageConfig is picked up on mount; the `full-page-config-loaded` variant
 * fires once at page load (before this app mounts), so it would leave pageConfig
 * undefined here and the model would never lock for non-owners.
 *
 * Returns false until pageConfig loads so the owner's own model never briefly
 * renders read-only, and false (for logged-in users) when the opened model
 * carries no owner id (uploaded models and not-yet-persisted generations),
 * which the current user implicitly owns. Also false during critical-CSS
 * extraction so the owner-only controls render and their styles are captured.
 */
function useOpenedModelReadOnly() {
  const { extractingCoreStyles } = useContext(ThreeDStudioPageContext)
  const pageConfig = usePageConfig()
  const ownerId = useThreeDStudioStore(state => state.ui?.openedModel?.userId)

  if (extractingCoreStyles || pageConfig === undefined) {
    return false
  }

  // Logged-out visitors can never own or persist a model, so everything is read-only.
  if (!pageConfig?.isCurrentUser) {
    return true
  }

  if (ownerId == null) {
    return false
  }

  return String(pageConfig.currentUserId) !== String(ownerId)
}

export default useOpenedModelReadOnly
